import { computed, ref, onMounted, watch, type Ref } from "vue";
import * as yup from "yup";
import { FormModes } from "@/types/form";
import { useTypeStore } from "@/store/store/typeStore";
import type { ApplicantAddress } from "@/types/applicant";
import type { FormSchema, FieldSchema, Option } from "@/models/form";
import type { LOSType } from "@/models/opal";
import { useForm } from "vee-validate";
import {
  getOptionById,
  getOptionLabel,
} from "@/composables/application/useApplicationForms";
import useAddressHelpers from "@/use/addressHelpers";
import useFormatter from "@/use/formatter";
import useValidation from "@/use/validation";
import useFunctions from "@/use/functions1";
import { AddressTypeIds } from "@/types/address";
import { ConsentServiceEntityTypeIds } from "@/types/consents";
import { Type } from "@/types/types";

export interface ValidationData {
  address: Ref<Record<any, any>>;
  initialValues: Ref<Record<any, any>>;
  mode: Ref<keyof typeof FormModes>;
  entityTypeId: Ref<number>;
  showUnitType: Ref<boolean>;
}

export function useAddressForm(data: ValidationData) {
  const typeStore = useTypeStore();

  const { verifySomeKeysAreDifferent } = useFunctions();

  const addressBackup = ref<ApplicantAddress | undefined>(undefined);
  const prefixes = ref<string[]>([]);
  const addressUnitTypePrefixes = computed<string[]>(
    () => typeStore.getAddressUnitTypePrefixes
  );
  const addressSchema = ref<FormSchema>({
    address1: {
      name: "address1",
      type: "text",
      subtype: "address",
      label: "Permanent Street Address",
      placeholder: "Street",
      required: true,
      maxLength: 50,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.address1 ? [errors.value.address1] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    addressUnitTypeId: {
      name: "addressUnitTypeId",
      type: "select",
      subtype: "address",
      label: "Unit Type",
      required: false,
      validate: (schema: FieldSchema) => {
        if (!data.showUnitType.value) {
          schema.errors = [];
          schema.valid = schema.errors.length === 0;
          schema.touched = schema.errors.length > 0;
          return;
        }

        schema.errors = errors.value?.addressUnitTypeId
          ? [errors.value.addressUnitTypeId]
          : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    address2: {
      name: "address2",
      type: "text",
      subtype: "address",
      label: "Unit Number",
      placeholder: "Unit #",
      required: false,
      maxLength: 25,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.address2 ? [errors.value.address2] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    city: {
      name: "city",
      type: "text",
      label: "City",
      subtype: "address",
      placeholder: "City",
      required: true,
      maxLength: 40,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.city ? [errors.value.city] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    stateId: {
      name: "stateId",
      type: "select",
      subtype: "address",
      label: "State",
      required: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.stateId ? [errors.value.stateId] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    postalCode: {
      name: "postalCode",
      type: "zipcode",
      label: "Zip Code",
      placeholder: "Zip Code",
      errorMessage: "Zip code is required.",
      title: "Zip Code Format: XXXXX or XXXXX-XXXX or XXXXXXXXX",
      required: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.postalCode
          ? [errors.value.postalCode]
          : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    countryId: {
      name: "countryId",
      type: "select",
      label: "Country",
      errorMessage: "Country is required.",
      required: false,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.countryId ? [errors.value.countryId] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
  });
  const { formatZipCode } = useFormatter();
  const { removePrefix } = useAddressHelpers();

  const { getErrorMessage, validZipFormat1Regex, validZipFormat2Regex } =
    useValidation();

  const addressUnitTypes = computed<LOSType[]>(
    () => typeStore.getAddressUnitTypes
  );

  const hasErrors = computed<boolean>(
    () => Object.keys(errors.value).length > 0
  );

  const unitTypeOptions = computed<Option[]>(
    () => typeStore.getAddressUnitTypeOptions
  );
  const statesOptions = computed<Option[]>(() => typeStore.getStateOptions);
  const countryOptions = computed<Option[]>(() => typeStore.getCountryOptions);

  const originalAddress = computed(() => {
    return {
      address1: data.initialValues.value?.address1 || "",
      address2: data.initialValues.value?.address2 || null,
      addressId: data.initialValues.value?.addressId || 0,
      addressTypeId:
        data.initialValues.value?.addressTypeId || AddressTypeIds.PERMANENT,
      addressTypeName: data.initialValues.value?.addressTypeName || null,
      addressUnitTypeId: data.initialValues.value?.addressUnitTypeId || null,
      addressUnitTypeName: data.initialValues.value?.addressUnitTypeName,
      applicantId: data.initialValues.value?.applicantId || 0,
      id: data.initialValues.value?.id || 0,
      city: data.initialValues.value?.city || "",
      stateId: data.initialValues.value?.stateId || 0,
      stateName: data.initialValues.value?.stateName,
      postalCode: data.initialValues.value?.postalCode,
      countryId: data.initialValues.value?.countryId || 0,
      countryName: data.initialValues.value?.countryName || "",
    };
  });

  const updatedAddress = computed(() => {
    return {
      address1: data.address.value?.address1,
      address2: data.showUnitType.value
        ? formattedAddress2.value
        : data.address.value?.address2?.trim(),
      addressId: data.address.value?.addressId || 0,
      addressTypeId:
        data.address.value?.addressTypeId || AddressTypeIds.PERMANENT,
      addressTypeName: data.address.value?.addressTypeName || "",
      addressUnitTypeId:
        data.address.value?.addressUnitTypeId !== null &&
        data.address.value?.addressUnitTypeId !== undefined &&
        data.address.value?.addressUnitTypeId > 0
          ? Number(data.address.value?.addressUnitTypeId)
          : null,
      addressUnitTypeName: data.address.value?.addressUnitTypeName,
      applicantId: data.address.value?.applicantId || 0,
      id: data.address.value?.id || 0,
      city: data.address.value?.city?.trim(),
      stateId: !isNaN(Number(data.address.value?.addressUnitTypeId))
        ? Number(data.address.value?.stateId)
        : null,
      stateName: data.address.value?.stateName,
      postalCode: data.address.value?.postalCode?.replace(/[^0-9]/g, ""),
      countryId: !isNaN(Number(data.address.value?.countryId))
        ? Number(data.address.value?.countryId)
        : 0,
      countryName: data.address.value?.countryName || "",
    };
  });

  const hasChanges = computed(() => {
    const compareKeys = [
      "address1",
      "address2",
      "addressUnitTypeId",
      "city",
      "stateId",
      "postalCode",
      "countryId",
    ];

    if (
      originalAddress.value?.addressId === 0 ||
      verifySomeKeysAreDifferent(
        originalAddress.value,
        updatedAddress.value,
        compareKeys
      )
    ) {
      return true;
    }
    return false;
  });

  const addressFormFields = computed(() => {
    const fields = [
      {
        name: addressSchema.value.address1.name,
        label: addressSchema.value.address1.label,
        type: addressSchema.value.address1.type,
        required: addressSchema.value.address1.required,
        value: data?.address.value?.address1,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
      },
      {
        name: addressSchema.value.addressUnitTypeId.name,
        label: addressSchema.value.addressUnitTypeId.label,
        type: addressSchema.value.addressUnitTypeId.type,
        required: addressSchema.value.addressUnitTypeId.required,
        value: data?.address.value?.addressUnitTypeId,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        options: unitTypeOptions.value,
      },
      {
        name: addressSchema.value.address2.name,
        label: addressSchema.value.address2.label,
        type: addressSchema.value.address2.type,
        required: addressSchema.value.address2.required,
        value: data?.address.value?.address2,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
      },
      {
        name: addressSchema.value.city.name,
        label: addressSchema.value.city.label,
        type: addressSchema.value.city.type,
        required: addressSchema.value.city.required,
        value: data?.address.value?.city,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
      },
      {
        name: addressSchema.value.stateId.name,
        label: addressSchema.value.stateId.label,
        type: addressSchema.value.stateId.type,
        required: true,
        value: data?.address.value?.stateId,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        options: statesOptions.value,
      },
      {
        name: addressSchema.value.postalCode.name,
        label: addressSchema.value.postalCode.label,
        type: addressSchema.value.postalCode.type,
        required: addressSchema.value.postalCode.required,
        value: data?.address.value?.postalCode,
        title: "Zip Code Format: XXXXX or XXXXX-XXXX  or XXXXXXXXX",
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
      },
      {
        name: addressSchema.value.countryId.name,
        label: addressSchema.value.countryId.label,
        type: addressSchema.value.countryId.type,
        required: addressSchema.value.countryId.required,
        value: data?.address.value?.countryId,
        width: "col-4",
        showOnAdd: false,
        showOnEdit: false,
        disabledOnEdit: false,
        options: countryOptions.value,
        visible: false,
      },
    ];

    return fields.filter((field) => {
      if (data.mode.value === "add") {
        return field.showOnAdd !== false;
      } else if (data.mode.value === "edit") {
        return field.showOnEdit !== false;
      }

      return true;
    });
  });

  const addressValidationSchema = computed<yup.Schema>(() => {
    return yup.object().shape({
      address1: yup
        .string()
        .label(
          addressSchema.value.address1?.label || "Permanent Street Address"
        )
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .required(getErrorMessage(addressSchema.value.address1))
        .test(
          "valid-input",
          `${addressSchema.value.address1.label} can only contain letters, numbers, apostrophes, hyphens, and spaces.`,
          function (value) {
            // Only allow standard A-Z a-z 0-9 values, single space, and - '
            const valid = /^[A-Za-z0-9-' ]+$/.test(value);

            return valid === true;
          }
        ),
      addressUnitTypeId: yup
        .number()
        .label(addressSchema.value?.addressUnitTypeId?.label || "Unit Type")
        .transform((value) => (isNaN(value) ? undefined : Number(value)))
        .test(
          "is-valid",
          getErrorMessage(addressSchema.value?.addressUnitTypeId),
          function (value, testContext) {
            if (!data.showUnitType.value) {
              return true;
            }
            const address2 = testContext.parent.address2;

            if (!address2) return true;

            if (
              address2 !== undefined &&
              address2 !== null &&
              (address2.toString() || "")?.trim()?.length > 0
            ) {
              return !isNaN(Number(value)) && Number(value) > 0;
            }

            return false;
          }
        ),
      address2: yup
        .string()
        .label(addressSchema.value.address2?.label || "Unit Number")
        .nullable()
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .test(
          "valid-unit-number",
          getErrorMessage(addressSchema.value.address2),
          function (value, testContext) {
            const hasInput =
              value !== undefined && value !== null && value?.trim().length > 0;

            if (!data.showUnitType.value) {
              // no unit type dropdown, this field is not required
              if (!hasInput) {
                return true;
              }
              // Only allow standard A-Z a-z 0-9 values and single space
              const specialCharacterCheck = /[^A-Za-z0-9\s]+$/.test(
                value ?? ""
              );

              if (specialCharacterCheck) {
                return testContext.createError({
                  message: `${addressSchema.value.address2.label} can only contain letters, numbers, and spaces.`,
                });
              }
            }

            const unitType = testContext.parent.addressUnitTypeId;

            if (!unitType && !hasInput) return true;

            if (
              hasInput ||
              (data.showUnitType.value &&
                unitType !== undefined &&
                unitType !== null &&
                !isNaN(Number(unitType)) &&
                Number(unitType) > 0)
            ) {
              if (!hasInput) {
                return testContext.createError({
                  message: `${addressSchema.value.address2.label} is a required field.`,
                });
              }
              // Only allow standard A-Z a-z 0-9 values and single space
              const specialCharacterCheck = /[^A-Za-z0-9\s]+$/.test(
                value ?? ""
              );

              if (specialCharacterCheck) {
                return testContext.createError({
                  message: `${addressSchema.value.address2.label} can only contain letters, numbers, and spaces.`,
                });
              }

              return true;
            }

            return false;
          }
        ),
      city: yup
        .string()
        .label(addressSchema.value.city?.label || "City")
        .required(getErrorMessage(addressSchema.value.city))
        .max(40)
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .test(
          "valid-input",
          `${addressSchema.value.city.label} can only contain letters, apostrophes, hyphens, and spaces.`,
          function (value) {
            // Only allow standard A-Z a-z values, apostrophes, single space, and - '
            const invalidCharCheck = /[^A-Za-z-' ]/.test(value);
            return invalidCharCheck === false;
          }
        ),
      stateId: yup
        .number()
        .label(addressSchema.value.stateId?.label || "State")
        .transform((value) => (isNaN(value) ? undefined : Number(value)))
        .required(getErrorMessage(addressSchema.value.stateId))
        .min(1, getErrorMessage(addressSchema.value.stateId)),
      postalCode: yup
        .string()
        .label(addressSchema.value.postalCode?.label || "Zip")
        .required(getErrorMessage(addressSchema.value.postalCode))
        .test(
          "is-valid-zip",
          "Please enter a valid ZIP code in 5-digit or 9-digit format (e.g., 12345, 12345-6789, or 123456789).",
          (value) => {
            // Handles 12345, 12345-6789
            const formatCheck1 = validZipFormat1Regex.test(value);
            // Handles 123456789
            const formatCheck2 = validZipFormat2Regex.test(value);

            if (value === undefined) {
              return false;
            } else if (value.length === 5 && formatCheck1) {
              return true;
            } else if (value.length === 10 && formatCheck1) {
              return true;
            } else if (value.length === 9 && formatCheck2) {
              return true;
            } else {
              return false;
            }
          }
        ),
    });
  });

  const address2 = computed<string | null>(() => {
    if (!data.showUnitType.value) {
      return values.address2;
    }

    if (
      selectedUnitType.value &&
      selectedUnitType.value?.abbreviation &&
      values.address2
    ) {
      return ` ${removePrefix(values.address2, addressUnitTypePrefixes.value)}`;
    }

    return null;
  });

  const formattedAddress2 = computed<string | null>(() => {
    if (
      selectedUnitType.value &&
      selectedUnitType.value?.abbreviation &&
      values.address2
    ) {
      return `${selectedUnitType.value?.abbreviation} ${removePrefix(
        values.address2,
        addressUnitTypePrefixes.value
      )}`;
    }
    return null;
  });

  const postalCode = computed<string>(() => {
    const cleanedPostalCode = data.address.value?.postalCode?.replace(
      /[^0-9]/g,
      ""
    );

    return cleanedPostalCode && cleanedPostalCode.length === 9
      ? cleanedPostalCode.slice(0, 5)
      : cleanedPostalCode;
  });

  const postalCodeExtension = computed<string | null>(() => {
    if ((data.address.value?.postalCode || "")?.length <= 5) {
      return null;
    }

    const cleanedPostalCodeExtension = data.address.value?.postalCode
      ?.split("-")[1]
      .replace(/[^0-9]/g, "");

    return cleanedPostalCodeExtension && cleanedPostalCodeExtension.length === 4
      ? cleanedPostalCodeExtension
      : cleanedPostalCodeExtension.length < 4 &&
        cleanedPostalCodeExtension.length > 0
      ? cleanedPostalCodeExtension
      : null;
  });

  const selectedUnitType = computed<LOSType | undefined>(() =>
    addressUnitTypes.value.find(
      (t: LOSType) => t.id === Number(data.address.value?.addressUnitTypeId)
    )
  );

  const { values, errors, setFieldValue, setValues, validateField } = useForm({
    validationSchema: addressValidationSchema,
  });

  function handleFieldChange(fieldName: string, value: any) {
    setFieldValue(fieldName, value, true);
    switch (fieldName) {
      case "addressUnitTypeId":
        data.address.value.addressUnitTypeName = getSelectedUnitType(value);
        break;
      case "stateId":
        data.address.value.stateName =
          getOptionLabel(statesOptions.value, Number(value)) || "";
        break;
      case "countryId":
        data.address.value.countryName =
          getOptionLabel(countryOptions.value, Number(value)) || "";
        break;

      default:
        return;
    }
  }

  function getSelectedUnitType(addressUnitTypeId: number | null) {
    const selectedUnitTypeId = addressUnitTypeId
      ? Number(addressUnitTypeId)
      : null;

    return selectedUnitTypeId
      ? addressUnitTypes.value?.find(
          (a: LOSType) => a.id === selectedUnitTypeId
        )?.name
      : null;
  }

  function formatAddress2(
    addressUnitTypeId: number | null,
    address2: string | null
  ) {
    const selectedUnitTypeId = addressUnitTypeId
      ? Number(addressUnitTypeId)
      : null;

    const selectedUnitType = selectedUnitTypeId
      ? addressUnitTypes.value?.find(
          (a: LOSType) => a.id === selectedUnitTypeId
        )
      : null;

    const prefixes = [] as string[];

    addressUnitTypes.value.forEach((au: any) => {
      prefixes.push(au?.abbreviation);
      prefixes.push(au?.name);
    });

    return selectedUnitTypeId
      ? `${selectedUnitType?.abbreviation} ${removePrefix(
          address2?.trim(),
          prefixes
        )}`
      : null;
  }
  function resetSchemas() {
    for (const property in addressSchema.value) {
      addressSchema.value[property].valid = true;
      addressSchema.value[property].errors = [];
      addressSchema.value[property].touched = false;
    }
  }
  function resetForm() {
    data.address.value = {
      address1: "",
      address2: null,
      addressId: data.initialValues.value?.addressId || 0,
      addressTypeId:
        data.initialValues.value?.addressTypeId || AddressTypeIds.PERMANENT,
      addressTypeName: data.initialValues.value?.addressTypeName || "",
      addressUnitTypeId: null,
      addressUnitTypeName: null,
      applicantId: data.initialValues.value?.applicantId || 0,
      id: data.initialValues.value?.id || 0,
      city: "",
      stateId: 0,
      stateName: "",
      postalCode: "",
      countryId: 0,
      countryName: "",
    };

    addressBackup.value = JSON.parse(JSON.stringify(data.address.value));
    data.address.value = JSON.parse(JSON.stringify(addressBackup.value));

    resetSchemas();
  }

  function setAddress() {
    if (data.initialValues.value) {
      addressBackup.value = JSON.parse(
        JSON.stringify(data.initialValues.value)
      );

      const selectedUnitType =
        data.initialValues.value?.addressUnitTypeId &&
        data.initialValues.value?.addressUnitTypeId > 0
          ? addressUnitTypes.value.find(
              (t: LOSType) =>
                t.id === Number(data.initialValues.value?.addressUnitTypeId)
            )
          : null;

      data.address.value = {
        address1: data.initialValues.value?.address1 || "",
        address2:
          data.entityTypeId.value === ConsentServiceEntityTypeIds.Draw
            ? data.initialValues.value?.address2
            : removePrefix(
                data.initialValues.value?.address2,
                addressUnitTypePrefixes.value
              ) || null,
        addressId: data.initialValues.value?.id || 0,
        addressTypeId: data.initialValues.value?.addressTypeId || 1,
        addressTypeName: data.initialValues.value?.addressTypeName || "",
        addressUnitTypeId: data.initialValues.value?.addressUnitTypeId,
        addressUnitTypeName: selectedUnitType?.name,
        applicantId: data.initialValues.value?.applicantId || 0,
        id: data.initialValues.value?.id || 0,
        city: data.initialValues.value?.city || "",
        stateId: data.initialValues.value?.stateId || 0,
        stateName: data.initialValues.value?.stateName || "",
        postalCode: data.initialValues.value?.postalCode
          ? formatZipCode(data.initialValues.value?.postalCode)
          : "",
        countryId: data.initialValues.value?.countryId || 0,
        countryName: data.initialValues.value?.countryName || "",
      };
    }
  }

  function discardChanges() {
    if (data.initialValues.value) {
      data.address.value = JSON.parse(JSON.stringify(addressBackup.value));
      addressBackup.value = JSON.parse(JSON.stringify(data.address.value));

      setAddress();
      resetSchemas();
    } else {
      resetForm();
    }
  }

  onMounted(async () => {
    await Promise.all([typeStore.getType(Type.AddressUnits)]);
  });

  watch(
    () => selectedUnitType.value,
    () => {
      data.address.value.addressUnitTypeName =
        selectedUnitType.value?.name || null;
    }
  );
  return {
    address2,
    addressBackup,
    addressFormFields,
    addressSchema,
    addressValidationSchema,
    addressUnitTypePrefixes,
    addressUnitTypes,
    countryOptions,
    discardChanges,
    errors,
    formatAddress2,
    formattedAddress2,
    hasChanges,
    hasErrors,
    handleFieldChange,
    originalAddress,
    postalCode,
    postalCodeExtension,
    prefixes,
    removePrefix,
    resetSchemas,
    selectedUnitType,
    setAddress,
    setFieldValue,
    setValues,
    statesOptions,
    updatedAddress,
    values,
    validateField,
  };
}
